import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { theme, type as text } from "../theme";
import { api } from "../api/client";
import { displayRef } from "./controlStatus";
import { ControlStatusBadge } from "./controlStatus";

// Findings across mapped standards (docs/CONTROL-MAPPINGS.md "Findings across mapped standards",
// backend lib/findingCrossChecks.ts). An open auditor or internal audit finding on one control is shown
// against the controls an approved mapping joins it to in the organisation's other standards — "check
// this here too". A pointer only: nothing is copied across.
//   * FindingAlsoCheck — on a finding: the mapped controls to look at.
//   * MappedFindingPointers — on a control: the findings elsewhere that map to it.
// Both read GET /control-mappings/finding-checks, once per page (cached for a short while).

interface Mapped { controlId: string; frameworkId: string; frameworkLabel: string; name: string; strength: string; strengthLabel: string; status: string | null; recordedMet: boolean }
interface Pointer { findingKey: string; source: "auditor" | "internal_audit"; label: string; fromControlId: string; fromFrameworkLabel: string; excerpt: string; href: string; strengthLabel: string }
interface CrossChecks { findings: Array<{ findingKey: string; mapped: Mapped[] }>; pointers: Record<string, Pointer[]> }

let cache: { at: number; promise: Promise<CrossChecks> } | null = null;
function loadAll(): Promise<CrossChecks> {
  if (!cache || Date.now() - cache.at > 30_000) {
    cache = { at: Date.now(), promise: api.get<CrossChecks>("/control-mappings/finding-checks").catch(() => ({ findings: [], pointers: {} })) };
  }
  return cache.promise;
}

/** On a finding card: the mapped controls in the organisation's other standards. Renders nothing when there are none. */
export function FindingAlsoCheck({ findingKey }: { findingKey: string }) {
  const [mapped, setMapped] = useState<Mapped[]>([]);
  useEffect(() => {
    let live = true;
    void loadAll().then((d) => { if (live) setMapped(d.findings.find((f) => f.findingKey === findingKey)?.mapped ?? []); });
    return () => { live = false; };
  }, [findingKey]);
  if (!mapped.length) return null;
  return (
    <div style={{ ...text.hint, marginTop: 6 }}>
      <span style={{ fontWeight: 600 }}>Check this here too:</span>{" "}
      {mapped.map((m, i) => (
        <span key={m.controlId}>
          {i > 0 && "; "}
          {m.frameworkLabel} <span style={{ ...text.ref, color: theme.color.ref }}>{displayRef(m.controlId)}</span> ({m.strengthLabel.toLowerCase()}
          {m.recordedMet ? ", recorded as met" : ""})
        </span>
      ))}
      . An approved mapping joins them; nothing is copied across.
    </div>
  );
}

/** On a control: the open findings on mapped controls in the organisation's other standards. */
export function MappedFindingPointers({ controlId, novice }: { controlId: string; novice?: boolean }) {
  const [pointers, setPointers] = useState<Pointer[]>([]);
  const [status, setStatus] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    setPointers([]);
    void loadAll().then((d) => {
      if (!live) return;
      setPointers(d.pointers[controlId] ?? []);
      setStatus(d.findings.flatMap((f) => f.mapped).find((m) => m.controlId === controlId)?.status ?? null);
    });
    return () => { live = false; };
  }, [controlId]);
  if (!pointers.length) return null;
  return (
    <div style={{ border: `1px solid ${theme.color.warning}`, background: theme.color.warningBg, borderRadius: 3, padding: "10px 14px", marginBottom: 12 }}>
      <div style={{ ...text.body, fontWeight: 600, marginBottom: 4, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        Check this here too: {pointers.length === 1 ? "a finding" : `${pointers.length} findings`} in another standard {pointers.length === 1 ? "maps" : "map"} to this control
        {status && <ControlStatusBadge status={status} novice={novice} />}
      </div>
      {pointers.map((p) => (
        <div key={p.findingKey} style={{ ...text.hint, marginTop: 4 }}>
          <Link to={p.href} style={{ color: theme.color.ref }}>{p.label}</Link> ({p.fromFrameworkLabel}; mapping: {p.strengthLabel.toLowerCase()}): {p.excerpt}
        </div>
      ))}
      <div style={{ ...text.hint, marginTop: 6 }}>The same weakness may be here. This control is still judged on its own requirement; nothing is copied from the finding.</div>
    </div>
  );
}
